import React from 'react';
import {Divider, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ManageClientService} from '@store/clients/services';

import {Sizes} from '@constants/sizes.constants';

import TextFieldComponent from '@components/inputs/textField/TextField.component';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';
import {map} from 'rxjs';
import {AdminRoutes, SuppliersRoutes} from '@utils/routes.utils';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {useNavigate} from 'react-router-dom';

interface ClientUnitFormProps {
  fromClient?: boolean;
  service: ManageClientService;
}

const ClientUnitForm = (props: ClientUnitFormProps) => {
  const {fromClient, service} = props;
  const navigate = useNavigate();

  const {t} = useTranslation();

  return (
    <>
      <Stack py="20px" spacing={2}>
        <Typography fontSize={16} fontWeight="700">
          {t('clients.details.contractedServices.manage.client')}
        </Typography>
        <Stack spacing={2}>
          {!fromClient && (
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('clients.details.contractedServices.manage.client')}
              value={service.contract?.clientOrganisationName || ''}
              readOnly
              required/>
          )}
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('clients.details.contractedServices.manage.clientUnit')}
            value={service.contract?.clientOrganisationUnitName || ''}
            readOnly
            required/>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('clients.details.contractedServices.manage.contract')}
            value={service.contract?.contractReference || ''}
            readOnly
            required/>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('clients.details.contractedServices.manage.additionalInformation')}
            value={service.contract?.datahubSpec?.additionalInformation || ''}
            readOnly
          />
          <AsyncSelectWithSearchComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('clients.details.contractedServices.manage.serviceKind')}
            placeholder={t('global.selectOptions')}
            required
            readOnly={true}
            value={service.kind}
            getOptions={(search) => filtersService.getFilters(TableFilterType.SERVICE_KIND, FilterBaseEntityEnum.NONE, [], search).pipe(
              map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
            )}/>
          <AsyncSelectWithSearchComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('clients.details.contractedServices.manage.waste')}
            placeholder={t('global.selectOption')}
            required={false}
            value={service.waste}
            readOnly
            getOptions={(search) => filtersService.getFilters(TableFilterType.WASTE, FilterBaseEntityEnum.NONE, [], search).pipe(
              map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
            )}/>
          <AsyncSelectWithSearchComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('clients.details.contractedServices.manage.container')}
            placeholder={t('global.selectOption')}
            required={false}
            value={service.container}
            readOnly
            getOptions={(search) => filtersService.getFilters(TableFilterType.CONTAINER, FilterBaseEntityEnum.NONE, [], search).pipe(
              map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
            )}/>
          <AsyncSelectWithSearchComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('clients.details.contractedServices.manage.supplierUnit')}
            placeholder={t('global.selectOption')}
            required
            readOnly={true}
            value={service.supplierUnit}
            getOptions={(search) => filtersService.getFilters(TableFilterType.SUPPLIER_ORGANISATION_UNIT, FilterBaseEntityEnum.NONE, [], search).pipe(
              map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
            )}/>
          {!!service.supplierService && (
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <InputLabel sx={{width: Sizes.drawerInputLabelWidth}} required={false}>
                {t('clients.details.contractedServices.manage.serviceSupplierDetailLabel')}*
              </InputLabel>
              <IconButton
                onClick={() => navigate(`/${AdminRoutes.SUPPLIERS}/${SuppliersRoutes.SERVICES}/${service.supplierService?.id}`)}>
                <OpenInNewIcon color="primary"/>
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
    </>
  );
};

export default ClientUnitForm;
